import React, { useCallback, useEffect, useRef, useState } from "react";
import Breadcrump from "../../components/Breadcrump";
import productAPI from "../../api/productApi";
import ProductCard from "./Card";
import Filtersidebar from "./detailfeatures/Filtersidebar";
import { FiFilter } from "react-icons/fi";

function ListPage() {
  const debounceRef = useRef(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState({});

  const [priceRange, setPriceRange] = useState([100, 10100]);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState({
    category: [],
    brand: [],
    min: "",
    max: "",
    sort: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);

  const observer = useRef();

  const fetchProducts = async (currentPage, appliedFilters = filters) => {
    setLoading(true);

    try {
      const res = await productAPI.listing({
        page: currentPage,
        ...appliedFilters,
      });

      if (res.data) {
        const { products, pagination } = res.data;

        setItems((prev) =>
          currentPage === 1 ? products || [] : [...prev, ...(products || [])],
        );
        setTotalCount(pagination?.totalCount || 0);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(page, appliedFilters);
  }, [page, appliedFilters]);

  const handleApplyFilters = () => {
    setItems([]);
    setPage(1);

    setAppliedFilters({
      ...filters,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
    });

    const offcanvasEl = document.getElementById("mobileFilter");

    if (offcanvasEl && window.bootstrap) {
      const offcanvas =
        window.bootstrap.Offcanvas.getOrCreateInstance(offcanvasEl);

      offcanvas.hide();

      setTimeout(() => {
        document
          .querySelectorAll(".offcanvas-backdrop")
          .forEach((el) => el.remove());

        document.body.classList.remove("offcanvas-backdrop", "modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 300);
    }
  };

  // filters api call
  const fetchFilters = async () => {
    try {
      const res = await productAPI.filters();

      if (res.data) {
        setFilterData(res.data);
        if (res.data?.priceRange) {
          setPriceRange([res.data.priceRange.min, res.data.priceRange.max]);
        }
      }
    } catch (err) {
      console.error("Filter API error:", err);
    }
  };

  useEffect(() => {
    fetchProducts(page, filters);
  }, [page, filters]);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    setItems([]);
    setPage(1);

    setFilters((prev) => ({
      ...prev,
      min: Number(priceRange[0]),
      max: Number(priceRange[1]),
    }));
  }, [priceRange]);

  // 🔥 Intersection Observer
  const lastItemRef = useCallback(
    (node) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && items.length < totalCount) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, items, totalCount],
  );

  const handleFilterChange = (key, value) => {
    setItems([]);
    setPage(1);

    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // checkbox
  const handleCheckboxChange = (key, value) => {
    setItems([]);
    setPage(1);

    setFilters((prev) => {
      const current = prev[key] || [];

      const exists = current.includes(value);

      return {
        ...prev,
        [key]: exists
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  const handlePriceChange = (range) => {
    setPriceRange(range); // UI update immediately

    // clear previous timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // wait for user to stop
    debounceRef.current = setTimeout(() => {
      setItems([]);
      setPage(1);

      setFilters((prev) => ({
        ...prev,
        minPrice: range[0],
        maxPrice: range[1],
      }));
    }, 500); 
  };

  return (
    <>
      <Breadcrump title="Products" />

      <section className="s-term-user flat-spacing-25">
        <div className="container">
          <div className="row">
            <div className="col-md-3 mb-4 d-none d-md-block">
              <Filtersidebar
                filterData={filterData}
                filters={filters}
                onFilterChange={handleFilterChange}
                onCheckboxChange={handleCheckboxChange}
                priceRange={priceRange}
                onPriceChange={handlePriceChange}
                onApply={handleApplyFilters}
              ></Filtersidebar>
            </div>

            {/* PRODUCTS GRID */}
            <div className="col-md-9">
              <button
                className="btn btn-dark rounded-0 mb-3 d-flex align-items-center gap-2 d-block d-md-none"
                data-bs-toggle="offcanvas"
                data-bs-target="#mobileFilter"
              >
                <FiFilter size={18} /> Filters
              </button>

              <div className="row">
                <div className="col-md-12">
                  <div className="blog-count text-left mb-4">
                    Total: <b>{totalCount}</b> categories
                  </div>
                </div>
              </div>

              <div className="row g-3">
                {items.map((item, index) => {
                  if (index === items.length - 1) {
                    return (
                      <div
                        className="col-lg-4 col-6"
                        key={item._id || item.id}
                        ref={lastItemRef}
                      >
                        <ProductCard item={item} />
                      </div>
                    );
                  }

                  return (
                    <div className="col-lg-4 col-6" key={item._id || item.id}>
                      <ProductCard item={item} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div
            className="offcanvas offcanvas-start d-md-none"
            tabIndex="-1"
            id="mobileFilter"
          >
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">
                <i className="fa fa-filter me-2"></i> Filters
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="offcanvas"
              ></button>
            </div>

            <div className="offcanvas-body">
              <Filtersidebar
                filterData={filterData}
                filters={filters}
                onFilterChange={handleFilterChange}
                onCheckboxChange={handleCheckboxChange}
                priceRange={priceRange}
                onPriceChange={handlePriceChange}
                onApply={handleApplyFilters}
              ></Filtersidebar>
            </div>
          </div>

          {loading && (
            <div className="text-center mt-4">
              <p>Loading more products...</p>
            </div>
          )}

          {/* End Message */}
          {!loading && items.length >= totalCount && (
            <div className="text-center mt-4">
              <p>✨ You’ve reached the end</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default ListPage;